import React, { useMemo, useState } from "react";
import MAP from "@/data/mpDistrictPaths.json";
import { formatINR } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";

const SCALE = ["#F1EDE4", "#FCE3CF", "#F8C49C", "#F09A62", "#E86A2E", "#B8461A"];
export const METRICS = ["demand", "msmes", "sanctioned_amount"];

const fmt = (metric, v) => (metric === "msmes" ? v : formatINR(v));

export function useDistrictIndex(rows) {
  return useMemo(() => Object.fromEntries((rows || []).map((r) => [r.district, r])), [rows]);
}

// Shapes for newer districts are drawn inside their parent; the parent's colour sums both.
export function shapeValue(index, shape, metric) {
  return [shape.name, ...shape.includes].reduce((s, n) => s + ((index[n] || {})[metric] || 0), 0);
}

export function MPDistrictMap({ rows, metric, selected, onSelect }) {
  const { t } = useLang();
  const [hover, setHover] = useState(null);
  const index = useDistrictIndex(rows);
  const max = Math.max(1, ...MAP.districts.map((d) => shapeValue(index, d, metric)));
  const color = (v) => (v <= 0 ? SCALE[0] : SCALE[Math.min(5, 1 + Math.floor((v / max) * 4.999))]);
  const hv = hover && MAP.districts.find((d) => d.name === hover);

  return (
    <div className="relative" data-testid="mp-map">
      <svg viewBox={`0 0 ${MAP.width} ${MAP.height}`} className="w-full h-auto" role="img" aria-label="Madhya Pradesh district map">
        {MAP.districts.map((d) => {
          const v = shapeValue(index, d, metric);
          const isSel = selected === d.name || d.includes.includes(selected);
          return (
            <path key={d.name} d={d.d} fill={color(v)} stroke={isSel ? "#0B1220" : "#ffffff"} strokeWidth={isSel ? 3 : 1.2}
              className="cursor-pointer transition-[fill,opacity] duration-200 hover:opacity-80" data-testid={`map-district-${d.name}`}
              onMouseEnter={() => setHover(d.name)} onMouseLeave={() => setHover(null)} onClick={() => onSelect(d.name)} />
          );
        })}
        {MAP.districts.map((d) => (
          <text key={`l-${d.name}`} x={d.cx} y={d.cy} textAnchor="middle" className="pointer-events-none select-none" fontSize="11" fill="#0B1220" opacity="0.7">{d.name}</text>
        ))}
      </svg>
      {hv && (
        <div className="absolute top-3 left-3 rounded-lg bg-[color:var(--bk-navy)] text-white px-3 py-2 text-xs shadow-lg pointer-events-none" data-testid="map-tooltip">
          <div className="font-semibold">{hv.name}{hv.includes.length ? ` + ${hv.includes.join(", ")}` : ""}</div>
          <div className="text-white/75">{t(`gmap_metric_${metric}`)}: {fmt(metric, shapeValue(index, hv, metric))}</div>
        </div>
      )}
      <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500">
        <span>{t("gm_low")}</span>
        {SCALE.map((c) => <span key={c} className="w-6 h-2.5 rounded-sm border border-slate-200" style={{ background: c }} />)}
        <span>{t("gm_high")}</span>
      </div>
    </div>
  );
}
